import React, { useState, useEffect, lazy, Suspense, startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import configService from '../services/configService';
import { useDataCache } from '../contexts/DataCacheContext';

const VoiceSelection = lazy(() => import('./VoiceSelection'));
const BusinessDescription = lazy(() => import('./BusinessDescription'));
const CallRoutingSettings = lazy(() => import('./CallRoutingSettings'));
const BusinessInfo = lazy(() => import('./BusinessInfo'));
const BehaviorSettings = lazy(() => import('./BehaviorSettings'));
const ChangePassword = lazy(() => import('./ChangePassword'));

function Settings({ adminBusinessId = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { cache, prefetchSettings, updateCache } = useDataCache();
  const [loading, setLoading] = useState(!cache.settings);
  const [businessConfig, setBusinessConfig] = useState(cache.settings?.businessConfig || null);
  const [activeTab, setActiveTab] = useState('business-info');
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSection, setExpandedSection] = useState('business-info');
  const [modules, setModules] = useState(cache.settings?.modules || {});

  // Use adminBusinessId if provided, otherwise use current user's ID
  const effectiveUserId = adminBusinessId || configService.getUserId();
  
  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    
    // Listen for tab switch events from BusinessDescription
    const handleTabSwitch = (e) => {
      setActiveTab(e.detail);
      if (window.innerWidth < 768) {
        setExpandedSection(e.detail);
      }
    };
    window.addEventListener('switchSettingsTab', handleTabSwitch);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('switchSettingsTab', handleTabSwitch);
    };
  }, []);

  useEffect(() => {
    // Set active tab based on URL only if URL changes
    const path = location.pathname;
    if (path === '/settings') {
      // Don't reset tab if already set
      setActiveTab(prev => prev || 'business-info');
    }
  }, [location.pathname]);

  useEffect(() => {
    // In admin mode, always load fresh - don't use cache
    if (adminBusinessId) {
      console.log('[Settings] Admin mode - loading fresh config, ignoring cache');
      loadBusinessConfig();
      loadModules();
      return;
    }
    
    // Normal mode - use cache if available
    if (cache.settings) {
      console.log('[Settings] Using cached settings');
      setBusinessConfig(cache.settings.businessConfig);
      setModules(cache.settings.modules);
      setLoading(false);
    } else if (!loading) {
      loadBusinessConfig();
      loadModules();
    }
  }, [cache.settings, adminBusinessId]);

  useEffect(() => {
    if (!cache.settings) {
      prefetchSettings();
    }
  }, []);

  const loadBusinessConfig = async () => {
    try {
      if (!effectiveUserId) {
        console.error('No user ID found, redirecting to setup');
        navigate('/setup');
        return;
      }
      
      console.log('[Settings] Loading config:', {
        adminBusinessId,
        effectiveUserId,
        willUseAdminEndpoint: !!adminBusinessId
      });
      
      // Use admin endpoint if adminBusinessId is provided
      const config = adminBusinessId 
        ? await configService.getAdminUserConfig(effectiveUserId)
        : await configService.getBusinessConfigByUserId(effectiveUserId);
      
      console.log('[Settings] Config loaded:', {
        business_id: config.business_id,
        business_name: config.business_name,
        cognito_user_id: config.cognito_user_id,
        usedAdminEndpoint: !!adminBusinessId,
        matchesUrlParam: config.business_id === adminBusinessId
      });
      
      setBusinessConfig(config);
      setLoading(false);
      
      // IMPORTANT: Don't cache admin-loaded configs
      if (!adminBusinessId) {
        console.log('[Settings] Caching config for current user');
        updateCache('settings', { businessConfig: config });
      } else {
        console.log('[Settings] NOT caching - admin mode, viewing different user');
      }
    } catch (error) {
      console.error('Failed to load business config:', error);
      setLoading(false);
      if (error.message.includes('404') || error.message.includes('Configuration not found')) {
        console.log('No configuration found, redirecting to setup');
        if (!adminBusinessId) {
          navigate('/setup');
        }
      }
    }
  };

  const loadModules = async () => {
    try {
      const response = await configService.getModules();
      const moduleMap = {};
      response.modules.forEach(m => {
        moduleMap[m.module_id] = m.enabled;
      });
      setModules(moduleMap);
    } catch (error) {
      console.error('Failed to load modules:', error);
    }
  };

  const getCurrentSubPage = () => {
    const subPages = {
      'business-info': { icon: 'fa-building', label: 'Business Info' },
      'behavior-settings': { icon: 'fa-cogs', label: 'Administrator Contact' },
      'password': { icon: 'fa-lock', label: 'Password' }
    };
    return subPages[activeTab] || subPages['business-info'];
  };

  return (
    <DashboardLayout currentSubPage={getCurrentSubPage()}>
      <div className="flex flex-1 flex-col md:flex-row">
        {/* Sub Menu */}
        <div className="w-full md:w-64 md:flex-shrink-0 md:mr-4">
          <div className="h-full p-2" style={{background: '#F5F5F7'}}>
            <nav className="space-y-1 flex flex-col md:flex-col overflow-x-visible">
              
              {/* Business Info */}
              <div>
                <button
                  onClick={() => {
                    startTransition(() => {
                      if (window.innerWidth < 768) {
                        setExpandedSection(expandedSection === 'business-info' ? null : 'business-info');
                      } else {
                        setActiveTab('business-info');
                      }
                    });
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                    (window.innerWidth < 768 ? expandedSection === 'business-info' : activeTab === 'business-info')
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa fa-building text-sm`} style={(window.innerWidth < 768 ? expandedSection === 'business-info' : activeTab === 'business-info') ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">Business Info</span>
                  </div>
                  <i className={`fa fa-chevron-${expandedSection === 'business-info' ? 'down' : 'right'} text-xs md:hidden`}></i>
                </button>
                {expandedSection === 'business-info' && window.innerWidth < 768 && (
                  <div className="mt-2 md:hidden">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">Business Information</div>
                          <p className="text-sm text-gray-600">Update your business details and contact information.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border">
                          <BusinessInfo onComplete={loadBusinessConfig} cachedBusinessConfig={adminBusinessId ? null : businessConfig} adminBusinessId={adminBusinessId} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Behavior Settings */}
              <div>
                <button
                  onClick={() => {
                    startTransition(() => {
                      if (window.innerWidth < 768) {
                        setExpandedSection(expandedSection === 'behavior-settings' ? null : 'behavior-settings');
                      } else {
                        setActiveTab('behavior-settings');
                      }
                    });
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                    (window.innerWidth < 768 ? expandedSection === 'behavior-settings' : activeTab === 'behavior-settings')
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa fa-cogs text-sm`} style={(window.innerWidth < 768 ? expandedSection === 'behavior-settings' : activeTab === 'behavior-settings') ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">Administrator Contact</span>
                  </div>
                  <i className={`fa fa-chevron-${expandedSection === 'behavior-settings' ? 'down' : 'right'} text-xs md:hidden`}></i>
                </button>
                {expandedSection === 'behavior-settings' && window.innerWidth < 768 && (
                  <div className="mt-2 md:hidden">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">Administrator Contact</div>
                          <p className="text-sm text-gray-600">Configure manager contact information and call handling preferences.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border">
                          <BehaviorSettings onComplete={loadBusinessConfig} adminBusinessId={adminBusinessId} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Password */}
              {!adminBusinessId && (
                <div>
                  <button
                    onClick={() => {
                      startTransition(() => {
                        if (window.innerWidth < 768) {
                          setExpandedSection(expandedSection === 'password' ? null : 'password');
                        } else {
                          setActiveTab('password');
                        }
                      });
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                      (window.innerWidth < 768 ? expandedSection === 'password' : activeTab === 'password')
                        ? 'text-white bg-[#3B3C42]'
                        : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <i className={`fa fa-lock text-sm`} style={(window.innerWidth < 768 ? expandedSection === 'password' : activeTab === 'password') ? {color: '#3395FF'} : {}}></i>
                      <span className="font-medium text-sm">Password</span>
                    </div>
                    <i className={`fa fa-chevron-${expandedSection === 'password' ? 'down' : 'right'} text-xs md:hidden`}></i>
                  </button>
                  {expandedSection === 'password' && window.innerWidth < 768 && (
                    <div className="mt-2 md:hidden">
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">Change Password</div>
                          <p className="text-sm text-gray-600">Update your account password.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border">
                          <ChangePassword />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </nav>
          </div>
        </div>

        {/* Main Content - Desktop Only */}
        <div className="hidden md:block flex-1 min-w-0">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          }>
            {activeTab === 'business-info' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-2">Business Information</div>
                    <p className="text-sm text-gray-600">Update your business details and contact information.</p>
                  </div>
                  <div className="bg-white p-8 rounded-2xl shadow-sm border">
                    <BusinessInfo onComplete={loadBusinessConfig} cachedBusinessConfig={adminBusinessId ? null : businessConfig} adminBusinessId={adminBusinessId} />
                  </div>
                </div>
              )}

              {activeTab === 'behavior-settings' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-2">Administrator Contact</div>
                    <p className="text-sm text-gray-600">Configure manager contact information and call handling preferences.</p>
                  </div>
                  <div className="bg-white p-8 rounded-2xl shadow-sm border">
                    <BehaviorSettings onComplete={loadBusinessConfig} adminBusinessId={adminBusinessId} />
                  </div>
                </div>
              )}

              {activeTab === 'password' && !adminBusinessId && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-2">Change Password</div>
                    <p className="text-sm text-gray-600">Update your account password.</p>
                  </div>
                  <div className="bg-white p-8 rounded-2xl shadow-sm border">
                    <ChangePassword />
                  </div>
                </div>
              )}
            </Suspense>
          </div>
      </div>
    </DashboardLayout>
  );
}

export default Settings;